import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { StatusBadge } from './status-badge';

// Stable id seed for instances that aren't given a testId (React 17 has no useId).
let instanceSeq = 0;

// Keys that open the listbox when it is closed.
const OPEN_KEYS = ['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
// Keys that, while open, drive option navigation (and need preventDefault).
const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

// Clamp an index into the [0, length - 1] range (no wrapping).
function clampIndex(index, length) {
	if (length <= 0) return 0;
	if (index < 0) return 0;
	if (index > length - 1) return length - 1;
	return index;
}

function VersionSelect({ label, value, options, onChange, testId }) {
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const triggerRef = useRef(null);
	const listboxRef = useRef(null);

	// Derive a stable id base from testId, or a per-instance fallback seed.
	const fallbackBase = useRef(null);
	if (fallbackBase.current === null) {
		instanceSeq += 1;
		fallbackBase.current = `dsv-compare-select-${instanceSeq}`;
	}
	const base = testId || fallbackBase.current;
	const labelId = `${base}-label`;
	const valueId = `${base}-value`;
	const listId = `${base}-list`;
	const optionId = (index) => `${base}-opt-${index}`;

	const hasOptions = options.length > 0;
	const selectedIndex = options.findIndex((opt) => opt.value === value);
	// value not in options -> fall back to the first option (if any).
	const selected = selectedIndex >= 0 ? options[selectedIndex] : options[0];
	const activeOptionId = open && hasOptions ? optionId(activeIndex) : undefined;

	// Close + return DOM focus to the trigger button.
	const closeAndFocusTrigger = () => {
		setOpen(false);
		if (triggerRef.current) triggerRef.current.focus();
	};

	// Open the listbox, seeding the active option index.
	const openWith = (nextActiveIndex) => {
		setActiveIndex(clampIndex(nextActiveIndex, options.length));
		setOpen(true);
	};

	const selectOption = (opt) => {
		if (opt) onChange(opt.value);
		closeAndFocusTrigger();
	};

	// Close on outside mousedown while open; clean up on close/unmount.
	useEffect(() => {
		if (!open) return undefined;
		const handleMouseDown = (event) => {
			const trigger = triggerRef.current;
			const listbox = listboxRef.current;
			if (trigger && trigger.contains(event.target)) return;
			if (listbox && listbox.contains(event.target)) return;
			setOpen(false);
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [open]);

	// Scroll the active option into view whenever it changes while open.
	useEffect(() => {
		const listbox = listboxRef.current;
		if (!open || !listbox) return;
		const node = listbox.ownerDocument.getElementById(optionId(activeIndex));
		if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, activeIndex]);

	const handleTriggerClick = () => {
		if (open) {
			closeAndFocusTrigger();
			return;
		}
		openWith(selectedIndex >= 0 ? selectedIndex : 0);
	};

	const handleKeyDown = (event) => {
		const { key } = event;
		if (!open) {
			if (!OPEN_KEYS.includes(key)) return;
			event.preventDefault();
			const lastIndex = options.length - 1;
			const current = selectedIndex >= 0 ? selectedIndex : 0;
			if (key === 'ArrowUp' || key === 'End') {
				openWith(lastIndex);
			} else {
				// Enter/Space/ArrowDown/Home -> current-or-first.
				openWith(key === 'Home' ? 0 : current);
			}
			return;
		}

		// Open: navigation + selection.
		if (NAV_KEYS.includes(key)) {
			event.preventDefault();
			if (key === 'ArrowDown') setActiveIndex((i) => clampIndex(i + 1, options.length));
			else if (key === 'ArrowUp') setActiveIndex((i) => clampIndex(i - 1, options.length));
			else if (key === 'Home') setActiveIndex(0);
			else if (key === 'End') setActiveIndex(options.length - 1);
			return;
		}
		if (key === 'Enter' || key === ' ') {
			event.preventDefault();
			selectOption(options[activeIndex]);
			return;
		}
		if (key === 'Escape') {
			event.preventDefault();
			closeAndFocusTrigger();
			return;
		}
		if (key === 'Tab') {
			// Let focus move naturally; just close.
			setOpen(false);
		}
	};

	return (
		<div className="dsv-compare__select">
			<span className="dsv-compare__select-label" id={labelId}>{label}</span>
			<button
				ref={triggerRef}
				type="button"
				className="dsv-compare__select-trigger"
				data-testid={testId}
				disabled={!hasOptions}
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-labelledby={`${labelId} ${valueId}`}
				aria-controls={listId}
				aria-activedescendant={activeOptionId}
				onClick={handleTriggerClick}
				onKeyDown={handleKeyDown}
			>
				<span className="dsv-compare__select-value" id={valueId}>
					{hasOptions && selected ? (
						<>
							{selected.label}
							<StatusBadge status={selected.status} />
						</>
					) : (
						'—'
					)}
				</span>
				<span className="dsv-compare__select-chevron" aria-hidden="true">
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
						<path d="M3 4.5 6 7.5 9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</span>
			</button>
			{open && hasOptions && (
				<ul
					ref={listboxRef}
					className="dsv-compare__listbox"
					id={listId}
					role="listbox"
					aria-labelledby={labelId}
					tabIndex={-1}
				>
					{options.map((opt, i) => (
						<li
							key={opt.value}
							id={optionId(i)}
							role="option"
							aria-selected={opt.value === value}
							className={classNames('dsv-compare__option', {
								'dsv-compare__option--active': i === activeIndex,
								'dsv-compare__option--selected': opt.value === value,
							})}
							onMouseEnter={() => setActiveIndex(i)}
							onClick={() => selectOption(opt)}
						>
							<span className="dsv-compare__option-label">{opt.label}</span>
							<StatusBadge status={opt.status} />
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

VersionSelect.propTypes = {
	label: PropTypes.string,
	value: PropTypes.string,
	options: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.string,
		label: PropTypes.string,
		status: PropTypes.string,
	})),
	onChange: PropTypes.func,
	testId: PropTypes.string,
};

VersionSelect.defaultProps = {
	label: '',
	value: '',
	options: [],
	onChange: () => {},
	testId: undefined,
};

export default VersionSelect;
